// components/OutletCodeCell.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Autocomplete, TextField, IconButton, Tooltip } from "@mui/material";
import debounce from "lodash.debounce";
import { Toaster } from '../../components/Toaster';   
import { getDCName, updatePOData, searchOutletCode, updateOutletMapping } from '../api/api';
import api from "../../axiosConfig";
import SearchIcon from "@mui/icons-material/Search";
import { getUser } from "../../helpers";

const OutletCodeCell = ({ value, filename, setPoData, platformName }) => {
  const [outletSearch, setOutletSearch] = useState(false);
  const [outletOptions, setOutletOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const user = getUser();

  const fetchOutletSuggestions = useMemo(
    () =>
      debounce(async (input) => {
        if (!input) return setOutletOptions([]);
        const results = await searchOutletCode(input, platformName);
        setOutletOptions(results);
      }, 300),
    [platformName]
  );

  useEffect(() => {
    if (searchInput) {
      fetchOutletSuggestions(searchInput);
    }
  }, [searchInput, fetchOutletSuggestions]);

  if (outletSearch) {
    return (
      <Autocomplete
        freeSolo
        disableClearable
        size="small"
        options={outletOptions}
        getOptionLabel={(option) => option?.label || ""}
        onInputChange={(e, newInputValue) => {
          setSearchInput(newInputValue);
        }}
        onChange={async (e, newValue) => {
          if (!newValue || !newValue.code) return;
          const selectedCode = newValue.code;

          try {
            await updatePOData({
              type: "header",
              filename,
              old_label: "OutletCode",
              label: "OutletCode",
              new_value: selectedCode,
              user
            });

            setPoData((prevPoData) => {
              // Update Outlet Code locally
              const updatedPoData = {
                ...prevPoData,
                ["Outlet Code"]: selectedCode,
                Header: {
                  ...prevPoData.Header,
                  OutletCode: selectedCode,
                },
              };

              // Call update-outlet-mapping endpoint
              const outletAddress = prevPoData?.Header?.["Shipped To Address"] || "";
              const platform = prevPoData?.Customer;
              if (platform && selectedCode && outletAddress) {
                updateOutletMapping({
                  Platform: platform,
                  Outlet_Code: selectedCode,
                  Outlet_Address: outletAddress,
                })
                  .then(() => {
                    console.log(
                      "outlet_code_mapping updated:",
                      selectedCode,
                      platform,
                      outletAddress
                    );
                    Toaster.success(
                      `outlet_code_mapping updated: ${selectedCode}, ${platform}, ${outletAddress}`
                    );
                  })
                  .catch((err) => {
                    console.error("Failed to update outlet mapping:", err);
                    Toaster.error("Failed to update outlet mapping");
                  });
              }

              return updatedPoData;
            });

            const response = await api.get("/get-dc-name", {
              params: { OutletCode: selectedCode },
            });
            const dcName = response.data?.[0] || "";

            await updatePOData({
              type: "header",
              filename,
              old_label: "DC Name",
              label: "DC Name",
              new_value: dcName,
              user
            });

            setPoData((prev) => ({
              ...prev,
              Header: {
                ...prev.Header,
                ["DC Name"]: dcName,
              },
            }));

            setOutletSearch(false);
            setSearchInput("");
          } catch (err) {
            console.error("Error updating OutletCode:", err);
            Toaster.error("Failed to update Outlet Code");
          }
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder="Enter Outlet Code/Outlet Name/Address" autoFocus />
        )}
      />
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
      <span>{value}</span>
      <Tooltip title="Search for outlet code">
        <IconButton
          onClick={() => setOutletSearch(true)}
          size="small"
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

};

export default OutletCodeCell;